/**
 * Triage inbox — the staff hot-path screen.
 *
 * Slice 5 rebuild: search + ⌘K hint, filter chip counts, row checkboxes
 * driving a bulk action bar, peek panel on row click, structured
 * columns, selected/peeked tints. INTERACTION_SPEC §10 keyboard
 * shortcuts integrate via useHotkeys (Esc = clear selection / close
 * peek; j/k nav lives in Stage 3).
 *
 * Click model (matches design handoff screen-triage.jsx):
 *   - Row click  → opens/selects ticket in the peek panel
 *   - Checkbox   → toggles bulk-selection (independent of peek)
 *   - Open icon (or ↵ in peek) → navigates to /tickets/T-####
 */

import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ExternalLink,
  Filter as FilterIcon,
  LayoutGrid,
  List as ListIcon,
  Search,
  Tag,
  User as UserIcon,
  X,
  Check,
  UserMinus,
} from 'lucide-react';
import { toast } from 'sonner';

import type { Ticket, TicketPriority, TicketStatus, Uuid } from '@beacon/core';

import { Avatar } from '@/components/beacon/avatar.js';
import { ContractLogo } from '@/components/beacon/contract-logo.js';
import { Priority } from '@/components/beacon/priority.js';
import { SlaTicker } from '@/components/beacon/sla-ticker.js';
import { Status } from '@/components/beacon/status.js';
import { Button } from '@/components/ui/button.js';
import { EmptyState } from '@/components/ui/empty-state.js';
import { SkeletonList } from '@/components/ui/skeleton.js';
import { useContractsById } from '@/hooks/use-contracts.js';
import { useFlashOnChange } from '@/hooks/use-flash-on-change.js';
import { useHotkeys } from '@/hooks/use-hotkeys.js';
import { useListNav } from '@/hooks/use-list-nav.js';
import { useNow } from '@/hooks/use-now.js';
import {
  useAssignTicket,
  useUpdateTicketPriority,
  useUpdateTicketStatus,
} from '@/hooks/use-ticket-mutations.js';
import { useTickets } from '@/hooks/use-tickets.js';
import { useAssignableUsers } from '@/hooks/use-users.js';
import { useViewer } from '@/hooks/use-viewer.js';
import { contractDisplay } from '@/lib/contract-display.js';
import { slaSnapshotFor } from '@/lib/sla-display.js';
import { cn } from '@/lib/cn.js';
import { NewTicketDialog } from './NewTicketDialog.js';
import { PeekPanel } from './PeekPanel.js';

type FilterId = 'urgent' | 'unassigned' | 'mine' | 'awaiting' | 'resolved' | 'all';
type ViewMode = 'list' | 'board';

interface FilterDef {
  id: FilterId;
  label: string;
  /** When true, active state uses the beacon-amber tint (the design
   *  reserves amber for "needs attention now"). */
  amber?: boolean;
}

const FILTERS: FilterDef[] = [
  { id: 'urgent', label: 'Urgent', amber: true },
  { id: 'unassigned', label: 'Unassigned' },
  { id: 'mine', label: 'Mine' },
  { id: 'awaiting', label: 'Awaiting client' },
  { id: 'all', label: 'All open' },
];

const BOARD_COLUMNS: Array<{ id: TicketStatus; label: string }> = [
  { id: 'new', label: 'New' },
  { id: 'triaged', label: 'Triaged' },
  { id: 'in_progress', label: 'In progress' },
  { id: 'awaiting_client', label: 'Awaiting client' },
  { id: 'resolved', label: 'Resolved' },
];

export interface TriageInboxProps {
  /**
   * 'all' = staff hot-path triage (default).
   * 'internal' = Conduct staff board — filtered to internal contracts
   * and defaults to the kanban view per SPEC §8.1.
   */
  scope?: 'all' | 'internal';
  /** Override the page title — defaults to "Inbox". */
  title?: string;
}

export function TriageInbox({
  scope = 'all',
  title,
}: TriageInboxProps = {}) {
  const navigate = useNavigate();
  const [newTicketOpen, setNewTicketOpen] = useState(false);
  // `filter` is a URL-synced value — derived from the `?filter=` query
  // param, written back when the user clicks a chip. Lets the sidebar's
  // "Breaching SLA" / "Awaiting client" links pre-apply a filter, and
  // makes any chip-narrowed inbox shareable + back-button-friendly.
  const [searchParams, setSearchParams] = useSearchParams();
  const rawFilter = searchParams.get('filter');
  const filter: FilterId = ((): FilterId => {
    if (
      rawFilter &&
      (FILTERS as readonly { id: string }[]).some((f) => f.id === rawFilter)
    ) {
      return rawFilter as FilterId;
    }
    return 'all';
  })();
  function setFilter(next: FilterId): void {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next === 'all') p.delete('filter');
        else p.set('filter', next);
        return p;
      },
      { replace: true }
    );
  }
  const [view, setView] = useState<ViewMode>(
    scope === 'internal' ? 'board' : 'list'
  );
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [peekedNumber, setPeekedNumber] = useState<string | null>(null);

  const tickets = useTickets();
  const contractsById = useContractsById();
  const viewer = useViewer().data;
  // Parent ticks at 30 s — fast enough that chip counts and the
  // urgent-filter membership stay essentially live, slow enough that
  // we don't reconcile the inbox tree 60 times a minute. The SLA pills
  // themselves use `<SlaTicker>` which subscribes to `useNow(1000)`
  // internally; only the pill re-renders second-by-second.
  const now = useNow(30_000);

  // Filter to scope first, then drop terminal states per view rules.
  const scopeFiltered = useMemo(
    () =>
      scope === 'internal'
        ? (tickets.data ?? []).filter((t) => {
            const c = contractsById.get(t.contractId);
            return c?.type === 'internal';
          })
        : (tickets.data ?? []),
    [scope, tickets.data, contractsById]
  );

  // Board view and 'resolved' filter show resolved tickets — drop
  // closed only. Other list views drop both resolved + closed.
  const allOpen = useMemo(
    () =>
      scopeFiltered.filter((t) =>
        view === 'board' || filter === 'resolved'
          ? t.status !== 'closed'
          : t.status !== 'resolved' && t.status !== 'closed'
      ),
    [scopeFiltered, view, filter]
  );

  // Per-filter counts, computed once and reused for the chip badges.
  const counts = useMemo(() => {
    const c: Record<FilterId, number> = {
      urgent: 0,
      unassigned: 0,
      mine: 0,
      awaiting: 0,
      resolved: 0,
      all: allOpen.length,
    };
    for (const t of allOpen) {
      const contract = contractsById.get(t.contractId);
      const snap = slaSnapshotFor(t, contract, now);
      if (t.priority === 'p1' || snap?.state === 'breaching') c.urgent += 1;
      if (t.assigneeUserId === null) c.unassigned += 1;
      if (viewer && t.assigneeUserId === viewer.userId) c.mine += 1;
      if (t.status === 'awaiting_client') c.awaiting += 1;
      if (t.status === 'resolved') c.resolved += 1;
    }
    return c;
  }, [allOpen, contractsById, now, viewer]);

  const matchesFilter = (t: Ticket): boolean => {
    const contract = contractsById.get(t.contractId);
    const snap = slaSnapshotFor(t, contract, now);
    if (filter === 'urgent')
      return t.priority === 'p1' || snap?.state === 'breaching';
    if (filter === 'unassigned') return t.assigneeUserId === null;
    if (filter === 'mine') return t.assigneeUserId === viewer?.userId;
    if (filter === 'awaiting') return t.status === 'awaiting_client';
    if (filter === 'resolved') return t.status === 'resolved';
    return true;
  };

  const matchesSearch = (t: Ticket): boolean => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    const contract = contractsById.get(t.contractId);
    return (
      t.number.toLowerCase().includes(q) ||
      t.subject.toLowerCase().includes(q) ||
      (contract?.name.toLowerCase().includes(q) ?? false)
    );
  };

  const filtered = useMemo(
    () => allOpen.filter((t) => matchesFilter(t) && matchesSearch(t)),
    // matchesFilter / matchesSearch close over stable values
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allOpen, filter, search, viewer, now]
  );

  // Esc clears selection or closes peek (peek wins if both open).
  useHotkeys({
    escape: () => {
      if (peekedNumber) {
        setPeekedNumber(null);
        return;
      }
      if (selectedIds.size) setSelectedIds(new Set());
    },
  });

  // j/k list navigation — only in list view (kanban defers to arrows
  // which handle left/right column switching naturally). Enter opens
  // the focused ticket. The peek-panel click syncs the cursor so
  // mouse + keyboard stay aligned.
  const navigableIds = useMemo(() => filtered.map((t) => t.id), [filtered]);
  const { focusedId, setFocusedId } = useListNav({
    ids: navigableIds,
    onOpen: (id) => {
      const t = filtered.find((x) => x.id === id);
      if (t) navigate(`/tickets/${t.number}`);
    },
    disabled: view !== 'list',
  });

  // Stable callbacks — Row is memo'd, so passing fresh closures every
  // render would defeat the memo (shallow-equal sees changed props).
  // useCallback keys these to the underlying setters, which React
  // guarantees stable.
  const toggleSelected = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const peekTicket = useCallback(
    (id: string, number: string) => {
      setPeekedNumber(number);
      setFocusedId(id);
    },
    [setFocusedId]
  );

  const openTicket = useCallback(
    (number: string) => {
      navigate(`/tickets/${number}`);
    },
    [navigate]
  );

  const peekedTicket = peekedNumber
    ? filtered.find((t) => t.number === peekedNumber) ??
      allOpen.find((t) => t.number === peekedNumber) ??
      null
    : null;

  return (
    <div className="flex flex-col h-full">
      <header className="h-12 px-6 border-b border-ink-600 bg-ink-850 flex items-center gap-3 shrink-0">
        <h1 className="text-[15px] font-semibold">
          {title ?? (scope === 'internal' ? 'Internal board' : 'Inbox')}
        </h1>
        <span className="text-[12px] text-ink-300 mono">
          {tickets.isLoading ? '…' : `${filtered.length} ${filter}`}
        </span>
        {tickets.isError && (
          <span className="text-[12px] mono text-danger-400">
            {tickets.error instanceof Error
              ? tickets.error.message
              : 'Failed to load'}
          </span>
        )}
        <SearchInput value={search} onChange={setSearch} />
        <div className="flex items-center gap-2">
          <ViewToggle value={view} onChange={setView} />
          <Button
            variant="primary"
            size="sm"
            onClick={() => setNewTicketOpen(true)}
            data-testid="triage-new-ticket"
          >
            + New ticket
          </Button>
        </div>
      </header>

      {/* Filter chips / bulk action bar — same slot, no layout shift */}
      <div className="border-b border-ink-600 shrink-0">
        {selectedIds.size > 0 ? (
          <BulkActionBar
            tickets={(tickets.data ?? []).filter((t) => selectedIds.has(t.id))}
            onClear={clearSelection}
          />
        ) : (
          <div className="px-6 py-2.5 flex items-center gap-2 bg-ink-900 overflow-x-auto">
            {FILTERS.map((f) => (
              <FilterChip
                key={f.id}
                def={f}
                active={filter === f.id}
                count={counts[f.id]}
                onClick={() => setFilter(f.id)}
              />
            ))}
            <div className="ml-auto text-[11.5px] text-ink-300 mono whitespace-nowrap">
              Sort: <span className="text-ink-200">SLA breaching first</span>
            </div>
          </div>
        )}
      </div>

      <div className="flex-1 min-h-0 flex">
        <div className="flex-1 min-w-0 flex flex-col">
          {view === 'list' ? (
            <>
              <ColumnHeader />
              <div className="flex-1 overflow-auto">
                {tickets.isLoading ? (
                  <SkeletonList count={8} rowHeight={56} />
                ) : filtered.length === 0 ? (
                  <EmptyTriageList
                    filter={filter}
                    hasSearch={Boolean(search.trim())}
                    onClearFilter={() => {
                      setFilter('all');
                      setSearch('');
                    }}
                  />
                ) : (
                  <ul className="divide-y divide-ink-600/60">
                    {filtered.map((t) => (
                      <Row
                        key={t.id}
                        t={t}
                        selected={selectedIds.has(t.id)}
                        peeked={peekedNumber === t.number}
                        focused={focusedId === t.id}
                        onPeek={peekTicket}
                        onToggle={toggleSelected}
                        onOpen={openTicket}
                      />
                    ))}
                  </ul>
                )}
              </div>
            </>
          ) : (
            <BoardView
              tickets={filtered}
              isLoading={tickets.isLoading}
            />
          )}
        </div>
        {peekedTicket && (
          <PeekPanel
            ticket={peekedTicket}
            onClose={() => setPeekedNumber(null)}
            onOpen={() => navigate(`/tickets/${peekedTicket.number}`)}
          />
        )}
      </div>
      <NewTicketDialog
        open={newTicketOpen}
        onClose={() => setNewTicketOpen(false)}
      />
    </div>
  );
}

/** Search input with ⌘K hint. Wires the Stage 3 palette later; for now
 *  the input itself filters the visible list locally. */
function SearchInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  return (
    <div className="relative flex-1 max-w-xl">
      <Search
        size={14}
        className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-300 pointer-events-none"
        aria-hidden="true"
      />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search tickets, clients, people…"
        aria-label="Search inbox"
        className="h-8 w-full bg-ink-800 border border-ink-600 rounded-md pl-8 pr-12 text-[13px] placeholder:text-ink-400 focus-ring"
      />
      <span
        className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-0.5"
        aria-hidden="true"
      >
        <kbd className="inline-flex items-center justify-center h-[18px] px-1 rounded border border-ink-600 bg-ink-900 text-[10px] mono text-ink-300">
          ⌘K
        </kbd>
      </span>
    </div>
  );
}

interface FilterChipProps {
  def: FilterDef;
  active: boolean;
  count: number;
  onClick: () => void;
}

function FilterChip({ def, active, count, onClick }: FilterChipProps) {
  const amber = def.amber;
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-[12.5px] font-medium border transition-colors focus-ring whitespace-nowrap',
        active
          ? amber
            ? 'bg-beacon-400/10 text-beacon-300 border-beacon-400/30'
            : 'bg-ink-100 text-ink-900 border-ink-100'
          : 'text-ink-200 border-ink-600 hover:bg-ink-800'
      )}
    >
      {def.label}
      <span
        className={cn(
          'mono text-[10.5px] px-1 rounded',
          active
            ? amber
              ? 'bg-beacon-400/20 text-beacon-300'
              : 'bg-ink-800 text-ink-200'
            : 'bg-ink-700 text-ink-300'
        )}
      >
        {count}
      </span>
    </button>
  );
}

/**
 * Bulk-action bar.
 *
 * Spec §3 (Bulk action) — N tickets each optimistically update; per-
 * ticket failures surface as a per-row red dot (deferred — needs a
 * `failedIds` channel from here back to <Row/>); aggregate result
 * shown in a single toast covering the whole batch.
 *
 * Implementation: fan-out via the per-ticket mutation hooks with the
 * `silent` flag set, so each rolls back on its own failure but the
 * single aggregate toast we emit here is the only visible signal.
 * `mutateAsync` so we can `Promise.allSettled` and count successes.
 */
function BulkActionBar({
  tickets,
  onClear,
}: {
  tickets: Ticket[];
  onClear: () => void;
}) {
  const status = useUpdateTicketStatus();
  const priority = useUpdateTicketPriority();
  const assign = useAssignTicket();
  const usersQuery = useAssignableUsers();

  // Inline pickers — Set Priority / Assign open small popovers
  // anchored to their button. `null` = closed.
  type PickerKind = 'priority' | 'assign' | null;
  const [picker, setPicker] = useState<PickerKind>(null);

  // Disable the bar while any of the three mutations are mid-flight.
  const isPending =
    status.isPending || priority.isPending || assign.isPending;

  async function fanOut<V extends { silent?: boolean }>(
    mutate: (vars: V) => Promise<unknown>,
    perTicketVars: (t: Ticket) => Omit<V, 'silent'>,
    successLabel: (n: number) => string,
    failureNoun: string
  ) {
    const total = tickets.length;
    const settled = await Promise.allSettled(
      tickets.map((t) =>
        mutate({ ...perTicketVars(t), silent: true } as V)
      )
    );
    const failed = settled.filter((r) => r.status === 'rejected').length;
    const ok = total - failed;
    if (failed === 0) {
      toast.success(successLabel(ok));
    } else if (ok === 0) {
      toast.error(`${failureNoun} failed`, {
        description: `All ${total} tickets failed to update — please retry.`,
      });
    } else {
      toast.warning(
        `${ok} of ${total} ${successLabel(ok).toLowerCase()}`,
        {
          description: `${failed} failed — please review and retry.`,
        }
      );
    }
    setPicker(null);
    onClear();
  }

  function bulkResolve() {
    return fanOut(
      status.mutateAsync,
      (t) => ({ number: t.number, status: 'resolved' as const }),
      (n) => `${n} ticket${n === 1 ? '' : 's'} marked resolved`,
      'Mark resolved'
    );
  }

  function bulkClose() {
    return fanOut(
      status.mutateAsync,
      (t) => ({ number: t.number, status: 'closed' as const }),
      (n) => `${n} ticket${n === 1 ? '' : 's'} closed`,
      'Close'
    );
  }

  function bulkPriority(p: TicketPriority) {
    return fanOut(
      priority.mutateAsync,
      (t) => ({ number: t.number, priority: p }),
      (n) =>
        `${n} ticket${n === 1 ? '' : 's'} set to ${p.toUpperCase()}`,
      'Set priority'
    );
  }

  function bulkAssign(userId: Uuid | null) {
    return fanOut(
      assign.mutateAsync,
      (t) => ({ number: t.number, assigneeUserId: userId }),
      (n) =>
        userId
          ? `${n} ticket${n === 1 ? '' : 's'} assigned`
          : `${n} ticket${n === 1 ? '' : 's'} unassigned`,
      userId ? 'Assign' : 'Unassign'
    );
  }

  return (
    <div
      role="region"
      aria-label="Bulk actions"
      className="px-6 py-2.5 bg-beacon-400/5 flex items-center gap-3 text-[12.5px] relative"
    >
      <span className="text-beacon-300 font-medium mono">
        {tickets.length} selected
      </span>
      <span className="h-4 w-px bg-ink-600" aria-hidden="true" />

      <BulkButton
        icon={<UserIcon size={13} />}
        label="Assign"
        active={picker === 'assign'}
        disabled={isPending}
        onClick={() =>
          setPicker((p) => (p === 'assign' ? null : 'assign'))
        }
      />

      <BulkButton
        icon={<Tag size={13} />}
        label="Set priority"
        active={picker === 'priority'}
        disabled={isPending}
        onClick={() =>
          setPicker((p) => (p === 'priority' ? null : 'priority'))
        }
      />

      <BulkButton
        icon={<Check size={13} />}
        label="Mark resolved"
        disabled={isPending}
        onClick={bulkResolve}
      />

      <BulkButton
        icon={<X size={13} />}
        label="Close"
        disabled={isPending}
        onClick={bulkClose}
        tone="danger"
      />

      <span className="ml-auto">
        <button
          onClick={onClear}
          disabled={isPending}
          className="text-ink-300 hover:text-ink-100 text-[12px] focus-ring rounded disabled:opacity-50"
        >
          Clear selection
        </button>
      </span>

      {picker === 'priority' && (
        <BulkPicker onClose={() => setPicker(null)}>
          {(['p1', 'p2', 'p3'] as const).map((p) => (
            <BulkPickerItem
              key={p}
              onClick={() => bulkPriority(p)}
              disabled={isPending}
            >
              <Priority p={p} />
              <span className="text-ink-300 text-[12px]">
                {p === 'p1'
                  ? 'Critical'
                  : p === 'p2'
                    ? 'High'
                    : 'Standard'}
              </span>
            </BulkPickerItem>
          ))}
        </BulkPicker>
      )}

      {picker === 'assign' && (
        <BulkPicker onClose={() => setPicker(null)}>
          <BulkPickerItem
            onClick={() => bulkAssign(null)}
            disabled={isPending}
          >
            <UserMinus size={14} className="text-ink-300" />
            <span className="text-ink-200 text-[13px]">Unassign</span>
          </BulkPickerItem>
          <div
            className="my-1 h-px bg-ink-700"
            aria-hidden="true"
          />
          {usersQuery.isLoading ? (
            <div className="px-3 py-2 text-[12px] text-ink-400">
              Loading…
            </div>
          ) : (
            (usersQuery.data ?? []).map((u) => (
              <BulkPickerItem
                key={u.id}
                onClick={() => bulkAssign(u.id as Uuid)}
                disabled={isPending}
              >
                <Avatar name={u.name} size={20} />
                <span className="text-ink-100 text-[13px] truncate">
                  {u.name}
                </span>
                <span className="text-ink-400 text-[11.5px] mono ml-auto truncate">
                  {u.email}
                </span>
              </BulkPickerItem>
            ))
          )}
        </BulkPicker>
      )}
    </div>
  );
}

/** Single button inside the bulk-action bar. */
function BulkButton({
  icon,
  label,
  active,
  disabled,
  onClick,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  tone?: 'danger';
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      className={cn(
        'inline-flex items-center gap-1.5 h-7 px-2 rounded-md text-[12.5px] focus-ring transition-colors disabled:opacity-50 disabled:pointer-events-none',
        tone === 'danger'
          ? 'text-danger-400 hover:bg-danger-500/10'
          : 'text-ink-200 hover:bg-ink-800',
        active && 'bg-ink-800 text-ink-100'
      )}
    >
      {icon}
      {label}
    </button>
  );
}

/** Lightweight popover anchored under the bulk bar. Click-outside +
 *  Esc close. Re-uses the dropdown-menu-in animation. */
function BulkPicker({
  children,
  onClose,
}: {
  children: React.ReactNode;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);
  return (
    <div
      ref={ref}
      role="menu"
      className="absolute left-6 top-full mt-1 z-30 min-w-[260px] max-w-[360px] bg-ink-800 border border-ink-600 rounded-md shadow-pop py-1 dropdown-menu-in"
    >
      {children}
    </div>
  );
}

function BulkPickerItem({
  children,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      disabled={disabled}
      className="w-full text-left px-3 py-1.5 flex items-center gap-2 hover:bg-ink-700 focus-ring disabled:opacity-50 disabled:pointer-events-none"
    >
      {children}
    </button>
  );
}

/** Column header strip — explains row layout, lives above the list. */
function ColumnHeader() {
  return (
    <div
      className="px-6 h-8 border-b border-ink-600 bg-ink-850 text-[10.5px] uppercase tracking-wider text-ink-300 font-semibold flex items-center gap-3 shrink-0"
      aria-hidden="true"
    >
      <div className="w-4" />
      <div className="hidden md:block w-[180px]">Contract</div>
      <div className="w-[58px] mono">Ticket</div>
      <div className="flex-1">Subject</div>
      <div className="hidden lg:block w-[110px]">Assignee</div>
      <div className="hidden md:block w-[40px] text-center">Pri</div>
      <div className="w-[120px]">Status</div>
      <div className="hidden lg:block w-[170px]">SLA</div>
      <div className="hidden md:block w-[60px] text-right">Updated</div>
      <div className="hidden md:block w-[20px]" />
    </div>
  );
}

interface RowProps {
  t: Ticket;
  selected: boolean;
  peeked: boolean;
  focused: boolean;
  /** All callbacks take ticket-identity args so the parent can pass
   *  stable (useCallback) refs and Row's memo actually pays off. */
  onPeek: (id: string, number: string) => void;
  onToggle: (id: string) => void;
  onOpen: (number: string) => void;
}

const Row = memo(function Row({
  t,
  selected,
  peeked,
  focused,
  onPeek,
  onToggle,
  onOpen,
}: RowProps) {
  const handlePeek = () => onPeek(t.id, t.number);
  const handleToggle = () => onToggle(t.id);
  const handleOpen = () => onOpen(t.number);
  const contractsById = useContractsById();
  const contract = contractsById.get(t.contractId);
  const display = contract
    ? contractDisplay(contract)
    : { tag: '??', color: '#4A4F5A' };
  // Flash the row when the visible state changes (status / priority /
  // assignee). Time-derived fields (deadlines, SLA snapshot) are
  // intentionally excluded — they'd retrigger on every minute tick.
  const flashing = useFlashOnChange(
    `${t.status}|${t.priority}|${t.assigneeUserId ?? ''}`
  );

  // Row uses a *plain* <li> + sibling controls (checkbox / peek button
  // / open button) so we satisfy:
  //   - axe `list` rule (only listitems inside <ul>)
  //   - axe `nested-interactive` rule (no interactive nested inside
  //     another interactive; the checkbox + open icon are siblings of
  //     the peek button, not children of it)
  //
  // Three independent visual states stack:
  //   - focused (j/k cursor)   → 2px left border in beacon-300
  //   - peeked (in side panel) → bg-beacon-400/7%
  //   - selected (checkbox)    → bg-beacon-400/4%
  const tintClass = peeked
    ? 'bg-beacon-400/[0.07]'
    : selected
      ? 'bg-beacon-400/[0.04]'
      : 'hover:bg-ink-850';

  return (
    <li
      className={cn(
        'flex items-center pl-6 pr-6 h-[56px] transition-colors relative flash-update',
        tintClass,
        // Focus indicator — left bar, doesn't shift layout.
        focused && 'before:absolute before:left-0 before:top-0 before:bottom-0 before:w-[2px] before:bg-beacon-300'
      )}
      data-flash={flashing ? 'on' : undefined}
    >
      <span
        className="w-4 h-4 flex items-center justify-center mr-3"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          type="checkbox"
          checked={selected}
          onChange={handleToggle}
          aria-label={`Select ${t.number}`}
          className="w-3.5 h-3.5 rounded border-ink-500 bg-ink-800 accent-beacon-400 focus-ring"
        />
      </span>
      <button
        type="button"
        onClick={handlePeek}
        onDoubleClick={handleOpen}
        aria-pressed={peeked}
        aria-label={`Peek ticket ${t.number}: ${t.subject}`}
        className="flex-1 min-w-0 flex items-center gap-3 h-full text-left focus-ring rounded-none cursor-pointer"
      >
        <span className="hidden md:flex w-[180px] items-center gap-2 min-w-0">
          <ContractLogo contract={display} size={22} />
          <span className="truncate text-[13px] text-ink-100">
            {contract?.name ?? '—'}
          </span>
        </span>
        <span className="w-[58px] mono text-[12px] text-ink-300 shrink-0">
          {t.number}
        </span>
        <span className="flex-1 min-w-0 truncate text-[13px] text-ink-100 font-medium">
          {t.subject}
        </span>
        <span className="hidden lg:flex w-[110px] items-center gap-1.5 min-w-0">
          {t.assigneeUserId ? (
            <>
              <Avatar name={t.assigneeUserId.replace('user-', '')} size={20} />
              <span className="text-[12px] text-ink-200 truncate">
                {t.assigneeUserId.replace('user-', '')}
              </span>
            </>
          ) : (
            <span className="text-[12px] text-ink-400 italic">Unassigned</span>
          )}
        </span>
        <span className="hidden md:flex w-[40px] justify-center">
          <Priority p={t.priority} />
        </span>
        <span className="w-[120px]">
          <Status s={t.status} />
        </span>
        <span className="hidden lg:block w-[170px]">
          <SlaTicker ticket={t} contract={contract} />
        </span>
        <span className="hidden md:block w-[60px] text-right text-[11.5px] mono text-ink-300 shrink-0">
          {formatRelative(t.createdAt)}
        </span>
      </button>
      <button
        type="button"
        onClick={handleOpen}
        aria-label={`Open ticket ${t.number}`}
        className="hidden md:inline-flex ml-3 w-[20px] h-[20px] items-center justify-center rounded text-ink-400 hover:text-ink-100 hover:bg-ink-700 focus-ring shrink-0"
      >
        <ExternalLink size={12} />
      </button>
    </li>
  );
});

function ViewToggle({
  value,
  onChange,
}: {
  value: ViewMode;
  onChange: (next: ViewMode) => void;
}) {
  return (
    <div className="inline-flex rounded-md border border-ink-600 overflow-hidden">
      <button
        onClick={() => onChange('list')}
        aria-pressed={value === 'list'}
        aria-label="List view"
        className={cn(
          'h-7 px-2 inline-flex items-center gap-1 text-[12px] focus-ring',
          value === 'list'
            ? 'bg-ink-700 text-ink-100'
            : 'text-ink-300 hover:bg-ink-800 hover:text-ink-100'
        )}
      >
        <ListIcon size={12} />
        List
      </button>
      <button
        onClick={() => onChange('board')}
        aria-pressed={value === 'board'}
        aria-label="Board view"
        className={cn(
          'h-7 px-2 inline-flex items-center gap-1 text-[12px] focus-ring border-l border-ink-600',
          value === 'board'
            ? 'bg-ink-700 text-ink-100'
            : 'text-ink-300 hover:bg-ink-800 hover:text-ink-100'
        )}
      >
        <LayoutGrid size={12} />
        Board
      </button>
    </div>
  );
}

/**
 * Empty state for the list view. Tone branches on what's actually
 * missing — search query, active filter, or genuinely no open tickets.
 */
function EmptyTriageList({
  filter,
  hasSearch,
  onClearFilter,
}: {
  filter: FilterId;
  hasSearch: boolean;
  onClearFilter: () => void;
}) {
  if (hasSearch) {
    return (
      <EmptyState
        tone="muted"
        icon={<Search size={20} />}
        heading="No results"
        sub="Tried tickets, contracts, and assignees. Closed tickets are excluded by default."
        actions={
          <Button variant="outline" size="sm" onClick={onClearFilter}>
            Clear search
          </Button>
        }
      />
    );
  }
  if (filter !== 'all') {
    return (
      <EmptyState
        tone="muted"
        icon={<FilterIcon size={20} />}
        heading="No tickets match this filter"
        sub="Switch back to All open or pick a different filter."
        actions={
          <Button variant="outline" size="sm" onClick={onClearFilter}>
            Clear filter
          </Button>
        }
      />
    );
  }
  return (
    <EmptyState
      tone="celebrate"
      icon={<Check size={22} />}
      heading="Inbox zero"
      sub="No open tickets. New ones will appear here as they arrive."
    />
  );
}

/**
 * Kanban view — same data, columns by status (SPEC Decision #8).
 * Drag-to-change-status is a v2 affordance; clicking a card lands on
 * the ticket detail where the status dropdown lives.
 */
function BoardView({
  tickets,
  isLoading,
}: {
  tickets: Ticket[];
  isLoading: boolean;
}) {
  const navigate = useNavigate();
  const byStatus = new Map<TicketStatus, Ticket[]>();
  for (const t of tickets) {
    const arr = byStatus.get(t.status) ?? [];
    arr.push(t);
    byStatus.set(t.status, arr);
  }

  return (
    <div className="flex-1 overflow-auto p-4">
      <div className="flex gap-3 min-w-max h-full">
        {BOARD_COLUMNS.map((col) => {
          const cards = byStatus.get(col.id) ?? [];
          return (
            <section
              key={col.id}
              className="w-72 shrink-0 flex flex-col rounded-md bg-ink-850 border border-ink-600 max-h-full"
            >
              <header className="px-3 py-2 border-b border-ink-600 flex items-center gap-2">
                <span className="text-[11px] uppercase tracking-[0.06em] text-ink-300 font-semibold">
                  {col.label}
                </span>
                <span className="text-[11px] mono text-ink-400">
                  {cards.length}
                </span>
              </header>
              <div className="flex-1 overflow-auto p-2 space-y-2">
                {isLoading ? (
                  <BoardCardSkeleton />
                ) : cards.length === 0 ? (
                  <div className="text-[11.5px] text-ink-400 px-2 py-1.5">
                    Empty
                  </div>
                ) : (
                  cards.map((t) => (
                    <BoardCard
                      key={t.id}
                      t={t}
                      onClick={() => navigate(`/tickets/${t.number}`)}
                    />
                  ))
                )}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}

const BoardCard = memo(function BoardCard({
  t,
  onClick,
}: {
  t: Ticket;
  onClick: () => void;
}) {
  const contractsById = useContractsById();
  const contract = contractsById.get(t.contractId);
  const display = contract
    ? contractDisplay(contract)
    : { tag: '??', color: '#4A4F5A' };

  return (
    <button
      onClick={onClick}
      className="block w-full text-left rounded-md border border-ink-600 bg-ink-800 hover:border-ink-500 hover:bg-ink-800/80 transition-colors focus-ring p-2.5"
    >
      <div className="flex items-center gap-1.5 mb-1.5">
        <ContractLogo contract={display} size={16} />
        <span className="mono text-[10.5px] text-ink-300">{t.number}</span>
        <Priority p={t.priority} />
        <span className="ml-auto">
          {t.assigneeUserId ? (
            <Avatar name={t.assigneeUserId.replace('user-', '')} size={18} />
          ) : (
            <span className="w-[18px] h-[18px] rounded-full border border-dashed border-ink-500 inline-block" />
          )}
        </span>
      </div>
      <div className="text-[12.5px] text-ink-100 leading-snug line-clamp-2">
        {t.subject}
      </div>
      <div className="mt-1.5">
        <SlaTicker ticket={t} contract={contract} hidePlaceholder />
      </div>
    </button>
  );
});

function BoardCardSkeleton() {
  return (
    <>
      {Array.from({ length: 3 }).map((_, i) => (
        <div
          key={i}
          className="h-16 rounded-md border border-ink-700 bg-ink-800 animate-pulse"
        />
      ))}
    </>
  );
}

/** Trivial relative date — replace with date-fns/dayjs when locale support needed. */
function formatRelative(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const ms = now.getTime() - d.getTime();
  const min = Math.floor(ms / 60_000);
  if (min < 1) return 'now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const dy = Math.floor(hr / 24);
  if (dy < 7) return `${dy}d ago`;
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short' });
}
